
import {SaveButton} from "../../../components/SaveButton";
import masks from "../../../masks";
import { dispatchLivewireEvent, refreshLivewireTree, restartLivewireTree } from "../../../Common/livewireCompat";

export default {
    init() {
        this.getServicesModal();
        this.getActionServiceModal();
        this.templateDelete();
        this.templateEdit();
        this.templateUpdate();
        this.templateView();
        this.initFormActions();
        this.sortServices();
        this.initReorderGrips();
        this.initServiceRowDrag();
        this.dropDownField();
        this.initInputMasks();
        this.confirmDeleteService();
        this.duplicateService();
        this.openServiceEditor();
        this.serviceEditorPage();
        this.addQuestionPanel();
    },

    /**
     * The add-question slide-over. Two steps in one surface — pick, then price it for THIS service —
     * so you cannot end up attached-but-unpriced, which is the hole the old checkbox list left.
     *
     * One search box does both jobs: matches are offered for reuse and a "Create X" card is always
     * present, so there is no new-vs-existing fork to choose before you know the answer.
     */
    addQuestionPanel() {
        const self = this;
        const panel = document.getElementById('addq');
        const dataEl = document.getElementById('addq-data');
        if (!panel || !dataEl) return;

        const library = JSON.parse(dataEl.textContent || '[]');
        const scrim = document.getElementById('addq-scrim');

        // The sidebar puts transform: translateX(120px) on the main container, which makes it a
        // containing block for position:fixed AND traps every z-index inside it — the panel came
        // out 120px off and underneath the navbar. Moving the overlay to <body> fixes both
        // properly; raising z-index would not have, because the trap is the transform.
        document.body.appendChild(scrim);
        document.body.appendChild(panel);
        const body = document.getElementById('addq-body');
        const foot = document.getElementById('addq-foot');
        const steps = document.getElementById('addq-steps');
        const titleEl = document.getElementById('addq-title');
        const esc = (s) => $('<div>').text(s == null ? '' : s).html();
        // Name the service the prices belong to, rather than saying "this service" at the exact
        // moment the whole point is that prices are per service.
        const serviceTitle = () => ($('#product-title').val() || '').trim() || 'this service';

        let step = 1, term = '', chosen = null, draft = null, pending = null, lastFocus = null;

        // A question is attached when its row is on the page — there is no checkbox any more.
        const attachedIds = () =>
            $('#svc-questions .bb-svc-q').map((i, el) => Number($(el).data('mod-id'))).get();

        const open = () => {
            step = 1; term = ''; chosen = null; draft = null; pending = null;
            lastFocus = document.activeElement;
            scrim.hidden = false; panel.hidden = false;
            requestAnimationFrame(() => { scrim.classList.add('is-on'); panel.classList.add('is-on'); });
            render();
            setTimeout(() => document.getElementById('addq-search')?.focus(), 60);
        };
        const close = () => {
            scrim.classList.remove('is-on'); panel.classList.remove('is-on');
            setTimeout(() => { scrim.hidden = true; panel.hidden = true; }, 200);
            lastFocus?.focus();
        };

        const render = () => (step === 1 ? renderPick() : renderPrice());

        /* ---- step 1: pick or create ---- */
        const renderPick = () => {
            titleEl.textContent = 'Add a question';
            steps.innerHTML = stepper(1);

            const on = attachedIds();
            const hits = library.filter(q => !on.includes(q.id) && q.title.toLowerCase().includes(term.toLowerCase()));
            const exact = library.some(q => q.title.toLowerCase() === term.trim().toLowerCase());

            let html = '<div class="bb-field">' +
                '<label for="addq-search">Search your questions, or type a new name</label>' +
                '<input class="bb-input" id="addq-search" placeholder="e.g. Home Size, Pets, Parking…" value="' + esc(term) + '">' +
                '</div>';

            if (hits.length) {
                html += '<div class="bb-over__group"><div class="bb-over__grouphead">Reuse one you already have</div>' + hits.map(q =>
                    '<button type="button" class="bb-pick' + (chosen === q.id ? ' is-sel' : '') + '" data-pick="' + q.id + '">' +
                        '<span class="bb-pick__nm">' + esc(q.title) + '</span>' +
                        '<span class="bb-pick__meta">' + q.options.length + ' options · ' +
                            (q.plan ? 'recurring plan' : (q.multiple ? 'customer picks several' : 'customer picks one')) + ' · ' +
                            (q.usedBy ? ('used by ' + q.usedBy + ' service' + (q.usedBy === 1 ? '' : 's')) : 'not used yet') + '</span>' +
                        '<span class="bb-pick__opts">' + q.options.slice(0, 3).map(esc).join(' · ') + (q.options.length > 3 ? ' …' : '') + '</span>' +
                    '</button>').join('') + '</div>';
            }

            // Always offered — never hidden behind an empty search box.
            if (!exact) {
                html += '<button type="button" class="bb-pick bb-pick--new" id="addq-new">' +
                    '<span class="bb-pick__nm">' + (term.trim() ? '+ Create “' + esc(term.trim()) + '”' : '+ Create a new question') + '</span>' +
                    // Three different situations, three different sentences. Saying "everything is
                    // already on this service" when the search simply found nothing is a lie.
                    '<span class="bb-pick__meta">' + (hits.length
                        ? 'You will add its options next. It becomes reusable on your other services.'
                        : (term.trim()
                            ? 'Nothing matches that name yet.'
                            : 'Every question you already have is on this service.')) + '</span></button>';
            }

            if (draft) html += newEditor();

            // Trap #4, made visible instead of silent.
            const willBe = on.length + 1;
            if (willBe > 4) {
                html += banner('amber', '<strong>This will be question #' + willBe + '.</strong> ' +
                    'Past four items the booking form splits into two steps for the customer. Nothing breaks, but preview it before you save.');
            }

            body.innerHTML = html;
            const ready = chosen || (draft && draft.title.trim() && draft.options.filter(o => o.trim()).length >= 2);
            foot.innerHTML = '<span class="bb-over__hint">' + (ready ? 'Prices are next — they all start at zero.' : 'Pick one, or type a new name.') + '</span>' +
                '<span class="bb-over__actions">' +
                '<button type="button" class="bb-btn bb-btn--ghost" data-addq="cancel">Cancel</button>' +
                '<button type="button" class="bb-btn bb-btn--primary" data-addq="next"' + (ready ? '' : ' disabled') + '>Next: price it</button>' +
                '</span>';
        };

        /* The shared stepper component, not a bespoke "1 Pick · 2 Price it" string. */
        const stepper = (at) =>
            '<div class="bb-step ' + (at > 1 ? 'complete' : 'active') + '">' +
                '<span class="bb-step__dot">' + (at > 1 ? '✓' : '1') + '</span> Pick</div>' +
            '<div class="bb-step ' + (at > 1 ? 'active' : '') + '">' +
                '<span class="bb-step__dot">2</span> Price it</div>';

        const banner = (tone, inner) =>
            '<div class="bb-banner bb-banner--' + tone + '"><span class="bb-banner__ic" aria-hidden="true">!</span><span>' + inner + '</span></div>';

        const newEditor = () => '<div class="bb-over__group">' +
            '<div class="bb-over__grouphead">New question</div>' +
            '<div class="bb-field"><label for="addq-newtitle">Question customers see</label>' +
            '<input class="bb-input" id="addq-newtitle" value="' + esc(draft.title) + '"></div>' +
            '<div class="bb-field"><label for="addq-picks">Customer picks</label>' +
            '<select class="bb-select" id="addq-picks" data-picks>' +
                '<option value="one"' + (draft.multiple ? '' : ' selected') + '>One answer</option>' +
                '<option value="several"' + (draft.multiple ? ' selected' : '') + '>Several answers</option>' +
            '</select></div>' +
            '<div class="bb-field"><label>Options</label>' +
            draft.options.map((o, i) => '<div class="bb-over__opt"><input class="bb-input" data-opt="' + i + '" placeholder="Option ' + (i + 1) + '" value="' + esc(o) + '">' +
                (draft.options.length > 2 ? '<button type="button" class="bb-close bb-close--s" data-optdel="' + i + '" aria-label="Remove option ' + (i + 1) + '">&times;</button>' : '') + '</div>').join('') +
            '</div>' +
            '<div><button type="button" class="bb-btn bb-btn--ghost bb-btn--s" id="addq-addopt">+ Add option</button></div>' +
            '<p class="bb-over__hint">Option names and their order are shared with every service using this question. Prices are not — you set those next, for this service only.</p>' +
            '</div>';

        /* ---- step 2: price it here ---- */
        const renderPrice = () => {
            titleEl.textContent = 'Price it for this service';
            steps.innerHTML = stepper(2);

            const q = pending;
            const amountHead = q.plan ? 'Discount' : 'Price';
            let html = '<div class="bb-over__qhead"><h4>' + esc(q.title) + '</h4>' +
                '<span>' + (q.plan ? 'recurring plan' : (q.multiple ? 'picks several' : 'picks one')) + '</span></div>';

            const others = q.usedBy || 0;
            html += banner('info', '<strong>Everything starts at zero.</strong> These prices belong to ' +
                '<em>' + esc(serviceTitle()) + '</em>. ' +
                (others
                    ? (others === 1
                        ? 'The one other service using this question keeps its own — nothing you type here touches it.'
                        : 'The ' + others + ' other services using this question keep their own — nothing you type here touches them.')
                    : 'No other service uses this question yet.'));

            if (q.elsewhere && q.elsewhere.length) {
                html += '<div class="bb-field"><label for="addq-copy">Start from another service’s prices?</label>' +
                    '<div class="bb-over__opt"><select class="bb-select" id="addq-copy"><option value="">Keep everything at zero</option>' +
                    q.elsewhere.map((e, i) => '<option value="' + i + '">' + esc(e.service) + '</option>').join('') +
                    '</select><button type="button" class="bb-btn bb-btn--ghost bb-btn--s" id="addq-docopy">Copy</button></div></div>';
            }

            // Same three-column grid, same header row, as a question row on the editor page.
            html += '<div class="bb-price-grid">' +
                '<div class="bb-price-grid__h">Option</div>' +
                '<div class="bb-price-grid__h bb-price-grid__h--num">' + amountHead + '</div>' +
                '<div class="bb-price-grid__h bb-price-grid__h--num">Extra time</div>' +
                q.options.map((opt, i) => {
                    const cell = (i === 0)
                        ? '<div class="bb-price-grid__cell"><span class="bb-price-grid__base">' + (q.plan ? 'full price' : 'starting price') + '</span></div>' +
                          '<div class="bb-price-grid__cell"><span class="bb-price-grid__base">—</span></div>'
                        : '<div class="bb-price-grid__cell">' +
                              (q.plan ? '' : '<span class="bb-affix" aria-hidden="true">$</span>') +
                              // inputmode drives BOTH the phone keypad and initInputMasks(): decimal
                              // for money/percent, numeric for whole minutes.
                              '<input class="bb-input" inputmode="decimal" data-amt="' + i + '" aria-label="' + amountHead + ' for ' + esc(opt) + '" value="' + (q.amounts[i] || 0) + '">' +
                              (q.plan ? '<span class="bb-affix" aria-hidden="true">% off</span>' : '') +
                          '</div>' +
                          '<div class="bb-price-grid__cell">' +
                              '<input class="bb-input" inputmode="numeric" data-min="' + i + '" aria-label="Extra time for ' + esc(opt) + '" value="' + (q.times[i] || 0) + '">' +
                              '<span class="bb-affix" aria-hidden="true">min</span>' +
                          '</div>';
                    return '<div class="bb-price-grid__opt">' + esc(opt) + '</div>' + cell;
                }).join('') + '</div>';

            html += '<p class="bb-over__hint">The first option is the starting price: the base price already covers it, so it never adds anything.</p>';

            body.innerHTML = html;
            foot.innerHTML = '<button type="button" class="bb-btn bb-btn--ghost" data-addq="back">&larr; Back</button>' +
                '<span class="bb-over__actions">' +
                '<button type="button" class="bb-btn bb-btn--ghost" data-addq="cancel">Cancel</button>' +
                '<button type="button" class="bb-btn bb-btn--primary" data-addq="add">Add to this service</button>' +
                '</span>';
        };

        /* ---- moving between the steps ---- */
        const toStep2 = async () => {
            if (chosen) {
                const q = library.find(x => x.id === chosen);
                pending = { ...q, amounts: q.options.map(() => 0), times: q.options.map(() => 0), isNew: false };
            } else {
                const options = draft.options.filter(o => o.trim());
                const res = await $.ajax({
                    type: 'POST', url: '/services-management/services/question/quick-create',
                    data: { _token: $('meta[name="csrf-token"]').attr('content'), title: draft.title.trim(), options, multiple: draft.multiple ? 1 : 0 },
                });
                pending = {
                    id: res.id, title: res.title, options, plan: false, multiple: draft.multiple,
                    usedBy: 0, elsewhere: [], amounts: options.map(() => 0), times: options.map(() => 0), isNew: true,
                };
                library.push({ ...pending, attached: false });
            }
            step = 2; render();
        };

        const addIt = async () => {
            const res = await $.ajax({
                type: 'GET',
                url: '/services-management/services/' + $('.bb-svc-editor').data('service-id') + '/question-row/' + pending.id,
            });
            const $row = $(res.html);
            // Carry the numbers typed in the panel into the row the server just rendered.
            pending.options.forEach((opt, i) => {
                if (i === 0) return;
                $row.find('[name$="[' + i + '][price]"], [name$="[' + i + '][percent]"]').val(pending.amounts[i] || 0);
                $row.find('[name$="[' + i + '][extra_time]"]').val(pending.times[i] || 0);
            });
            // Into the group the customer will actually show it in.
            const groupKey = pending.multiple ? 'many' : 'one';
            const $group = $('#svc-questions .bb-svc-qgroup[data-qgroup="' + groupKey + '"]');
            $group.prop('hidden', false).find('.bb-svc-qgroup__rows').append($row);
            close();
            $('#svc-questions').trigger('svc:recount');
            $('#svc-dirty').prop('hidden', false);
            // Same reason as the nav: smooth scrollIntoView can silently do nothing.
            // `_this` belongs to serviceEditorPage(); referencing it here threw a ReferenceError
            // on every add, so the page never scrolled to the row it had just created.
            setTimeout(() => self.scrollToEl && self.scrollToEl($row[0]), 240);
        };

        /* ---- wiring ---- */
        $('#svc-add-question').on('click', open);
        $('#addq-close, #addq-scrim').on('click', close);
        $(document).on('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) close(); });

        $(panel).on('input', '#addq-search', function () {
            term = this.value;
            const pos = this.selectionStart;
            renderPick();
            const el = document.getElementById('addq-search');
            if (el) { el.focus(); el.setSelectionRange(pos, pos); }
        });
        $(panel).on('click', '[data-pick]', function () {
            const id = Number($(this).data('pick'));
            chosen = chosen === id ? null : id; draft = null; renderPick();
        });
        $(panel).on('click', '#addq-new', () => { chosen = null; draft = { title: term.trim(), multiple: false, options: ['', ''] }; renderPick(); });
        $(panel).on('input', '#addq-newtitle', function () { draft.title = this.value; $('[data-addq="next"]').prop('disabled', !(draft.title.trim() && draft.options.filter(o => o.trim()).length >= 2)); });
        $(panel).on('input', '[data-opt]', function () { draft.options[$(this).data('opt')] = this.value; $('[data-addq="next"]').prop('disabled', !(draft.title.trim() && draft.options.filter(o => o.trim()).length >= 2)); });
        $(panel).on('click', '[data-optdel]', function () { draft.options.splice($(this).data('optdel'), 1); renderPick(); });
        $(panel).on('click', '#addq-addopt', () => { draft.options.push(''); renderPick(); });
        $(panel).on('change', 'select[data-picks]', function () { draft.multiple = this.value === 'several'; });
        $(panel).on('input', '[data-amt]', function () { pending.amounts[$(this).data('amt')] = parseFloat(this.value) || 0; });
        $(panel).on('input', '[data-min]', function () { pending.times[$(this).data('min')] = parseInt(this.value, 10) || 0; });
        $(panel).on('click', '#addq-docopy', () => {
            const pick = $('#addq-copy').val();
            if (pick === '') return;
            const src = pending.elsewhere[pick];
            pending.options.forEach((o, i) => {
                const row = src.values[i] || {};
                pending.amounts[i] = Math.abs(parseFloat(row.price ?? row.percent ?? 0)) || 0;
                pending.times[i] = parseInt(row.extra_time, 10) || 0;
            });
            renderPrice();
        });
        $(panel).on('click', '[data-addq]', function () {
            const action = $(this).data('addq');
            if (action === 'cancel') return close();
            if (action === 'back') { step = 1; return render(); }
            if (action === 'next') return toStep2();
            if (action === 'add') return addIt();
        });
    },

    /** The row itself opens the service, which is why the pencil is gone from the actions. */
    openServiceEditor() {
        // Put an archived service back. The row cannot open the editor while it is archived, so
        // Restore is the only thing on it.
        $('body').off('click', '.bb-svc-restore').on('click', '.bb-svc-restore', function () {
            const btn = $(this).attr('disabled', true);
            $.ajax({
                method: 'POST',
                url: '/services-management/services/restore-service/' + $(this).data('service-id'),
                data: { _token: $('meta[name="csrf-token"]').attr('content') },
                success: () => window.location.reload(),
                error: () => btn.removeAttr('disabled'),
            });
        });

        $('body').off('click', '.bb-svc-row')
            .on('click', '.bb-svc-row', function (e) {
                // An archived service has no editor page to open — its route cannot resolve it.
                if ($(this).hasClass('is-archived')) return;
                if ($(e.target).closest('button, a, input, label, .bb-svc-row__grip').length) return;
                let id = $(this).data('id');
                if (id) window.location = '/services-management/services/' + id + '/edit';
            });
    },

    /**
     * The editor page: section nav, dirty flag, dependent sections, and save. The old modal saved
     * and then called $('#product_table #table').DataTable().ajax.reload() — a table that is not on
     * this page, so the list silently never refreshed.
     */
    serviceEditorPage() {
        let form = $('#product-form');
        if (!form.length || !$('.bb-svc-editor').length) return;
        let _this = this;

        const markDirty = () => $('#svc-dirty').prop('hidden', false);
        form.on('input change', 'input, select, textarea', markDirty);

        // How far down the page is covered, so a jumped-to heading is never hidden under it.
        //
        // This counted the save bar's height alone. The bar is sticky at `top: var(--bb-header-h)`
        // — it parks BELOW the fixed app navbar — so the thing actually covering the heading is the
        // navbar plus the bar, and every jump landed the section title underneath them. Read the
        // sticky offset rather than hard-coding 60px, and leave real breathing room under it.
        // On narrow widths the bar goes `position: static`, `top` computes to auto, and the offset
        // correctly collapses to just the bar.
        const barOffset = () => {
            const bar = document.querySelector('.bb-svc-editor__bar');
            if (!bar) return 16;
            const stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
            return stickyTop + bar.getBoundingClientRect().height + 24;
        };

        /**
         * scrollIntoView({behavior:'smooth'}) is a silent no-op in some browsers — it was here,
         * so every nav link did nothing at all while still swallowing the click. Ask for smooth,
         * then make sure we actually arrived.
         */
        const scrollToEl = (target) => {
            if (!target) return;
            const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - barOffset());
            try {
                window.scrollTo({ top, behavior: 'smooth' });
            } catch (e) {
                window.scrollTo(0, top);
            }
            setTimeout(() => {
                if (Math.abs(window.scrollY - top) > 4) window.scrollTo(0, top);
            }, 400);
        };
        this.scrollToEl = scrollToEl;

        // Section nav highlights whatever you are actually looking at.
        const links = $('#svc-nav a');
        links.on('click', function (e) {
            e.preventDefault();
            // Mark it active on the click itself rather than waiting for a scroll event to tell us
            // where we ended up: it is what you expect from clicking a nav item, and it means the
            // primary interaction never depends on scroll events firing.
            links.removeClass('is-on');
            $(this).addClass('is-on');
            scrollToEl(document.querySelector($(this).attr('href')));
        });
        /**
         * Which section you are actually looking at.
         *
         * This was an IntersectionObserver with a percentage rootMargin, and it never updated the
         * nav once — several sections satisfy the margin at the same time and whichever entry the
         * callback happened to process last won. Working it out from scroll position is
         * deterministic: the active section is the last one whose top has passed under the bar,
         * and the final section wins outright once the page is scrolled to the bottom.
         */
        const sections = Array.from(document.querySelectorAll('.bb-svc-card'));
        const syncNav = () => {
            if (!sections.length) return;
            const line = barOffset() + 8;
            const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
            let active = sections[0];
            if (atBottom) {
                active = sections[sections.length - 1];
            } else {
                sections.forEach((s) => {
                    if (s.getBoundingClientRect().top <= line) active = s;
                });
            }
            links.removeClass('is-on');
            links.filter('[href="#' + active.id + '"]').addClass('is-on');
        };
        $(window).on('scroll.svcnav resize.svcnav', syncNav);
        syncNav();

        // Tips only mean anything when payment is required; a rate only when not exempt.
        $('#service_payment_check').on('change', function () {
            $('.tips-block').toggleClass('d-none', !this.checked);
        });
        $('#service_tax_check').on('change', function () {
            $('.tax-container').toggleClass('d-none', this.checked);
        });
        $('#checklist_select_show').on('change', function () {
            $('.checklist-container').toggleClass('d-none', !this.checked);
        });

        // Keep the "this splits the form in two" warning and the nav count honest. A question is
        // attached when its row exists, so the count is simply how many rows are on the page.
        const recount = () => {
            let n = $('#svc-questions .bb-svc-q').length;
            $('#svc-qcount').text(n);
            $('#nav-questions').text(n);
            $('#svc-complexity').prop('hidden', n <= 4);
            $('#svc-questions-empty').prop('hidden', n > 0);
        };
        $('#svc-questions').on('svc:recount', recount);
        recount();

        // Remove takes the question off THIS service. It is reversible until you leave the page —
        // nothing is written until Save, and saveService() syncs to the rows that are posted.
        // Break away from a shared question. Saves first is not required — the copy is made
        // server-side against the saved link — but the page must reload so the row shows the new
        // name and drops the sharing chip.
        $('#svc-questions').on('click', '.bb-svc-q__detach', function () {
            const used = Number($(this).data('used-by') || 1);
            const title = $(this).data('mod-title');
            if (!window.confirm(
                'Give this service its own copy of "' + title + '"?\n\n'
                + 'It stops following the shared question, so changes here will not affect the other '
                + (used - 1) + ' service' + (used - 1 === 1 ? '' : 's') + ' — and changes there will '
                + 'no longer reach this one. Its current options and prices are kept.')) {
                return;
            }
            const btn = $(this).attr('disabled', true);
            $.ajax({
                method: 'POST',
                url: window.location.pathname.replace(/\/edit$/, '') + '/question/detach',
                data: {
                    modifier_id: $(this).data('mod-id'),
                    _token: $('meta[name="csrf-token"]').attr('content'),
                },
                success: () => window.location.reload(),
                error: () => btn.removeAttr('disabled'),
            });
        });

        // Editing a shared question edits it for every service using it. Say so before they leave
        // this page, because the page they land on gives no hint that anyone else depends on it.
        $('#svc-questions').on('click', '.bb-svc-q__edit', function (e) {
            const used = Number($(this).data('used-by') || 1);
            if (used > 1 && !window.confirm(
                '"' + $(this).data('mod-title') + '" is used by ' + used + ' services.\n\n'
                + 'Changing its options, their order, or how many can be picked changes it for all '
                + used + '. Each service keeps its own prices.\n\nOpen it anyway?')) {
                e.preventDefault();
            }
        });

        $('#svc-questions').on('click', '.bb-svc-q__remove', function () {
            // Removing is only ever local — it detaches this one service. Worth saying, because the
            // same word on the Attributes page deletes the question for everybody.
            const used = Number($(this).data('used-by') || 1);
            if (used > 1 && !window.confirm(
                'Take "' + $(this).data('mod-title') + '" off this service?\n\n'
                + 'The other ' + (used - 1) + ' service' + (used - 1 === 1 ? '' : 's') + ' using it '
                + (used - 1 === 1 ? 'is' : 'are') + ' not affected, and the question itself is kept.')) {
                return;
            }
            $(this).closest('.bb-svc-q').remove();
            const group = $(this).closest('.bb-svc-qgroup');
            group.prop('hidden', group.find('.bb-svc-q').length === 0);
            recount();
            markDirty();
        });

        /**
         * Drag to reorder. The order is real: saveService() writes modifier_in_product_sort from
         * the posted key order, and Product::modifierProducts() orders by it — so the customer
         * sees this order. Dragging is confined to the row's own group because the booking form
         * lays the two groups out differently (single-answer questions share one row, multi-answer
         * ones each get a block), so moving a row across groups could not be honoured.
         */
        let dragged = null;

        // Move up / down on the question rows. Same reorder, without needing to see the drop edge.
        $('#svc-questions').on('click', '.svc-q-move', function () {
            const row = $(this).closest('.bb-svc-q');
            const up = $(this).data('sort-direction') === 'up';
            const sibling = up ? row.prev('.bb-svc-q') : row.next('.bb-svc-q');
            if (!sibling.length) return;
            up ? sibling.before(row) : sibling.after(row);
            $('.bb-grip').removeClass('is-open').find('.bb-grip__menu').prop('hidden', true);
            markDirty();
        });

        $('#svc-questions')
            .on('dragstart', '.bb-grip__handle', function (e) {
                dragged = $(this).closest('.bb-svc-q')[0];
                dragged.classList.add('is-dragged');
                e.originalEvent.dataTransfer.effectAllowed = 'move';
                // Firefox will not start a drag without payload.
                e.originalEvent.dataTransfer.setData('text/plain', dragged.dataset.modId || '');
            })
            .on('dragend', '.bb-grip__handle', function () {
                if (dragged) dragged.classList.remove('is-dragged');
                dragged = null;
            })
            .on('dragover', '.bb-svc-qgroup__rows', function (e) {
                if (!dragged) return;
                // Same group only.
                if (!this.contains(dragged)) return;
                e.preventDefault();
                e.originalEvent.dataTransfer.dropEffect = 'move';

                const y = e.originalEvent.clientY;
                const after = [...this.querySelectorAll('.bb-svc-q:not(.is-dragged)')].find((row) => {
                    const box = row.getBoundingClientRect();
                    return y < box.top + box.height / 2;
                });
                if (after) {
                    if (after !== dragged.nextSibling) this.insertBefore(dragged, after);
                } else if (this.lastElementChild !== dragged) {
                    this.appendChild(dragged);
                }
            })
            .on('drop', '.bb-svc-qgroup__rows', function (e) {
                if (!dragged || !this.contains(dragged)) return;
                e.preventDefault();
                markDirty();
            });

        $('#service-submit-form').on('click', function (e) {
            e.preventDefault();
            _this.SaveBtn = new SaveButton();
            let data = new FormData(form[0]);
            if ($('input[name=tax_exempt]:checked').length) data.delete('tax_id');
            $('.bb-workers-select .worker-row input[type=checkbox]:checked').each(function () {
                data.append('workers[]', $(this).attr('id'));
            });
            $.ajax({
                type: 'POST',
                url: '/services-management/services/save-service',
                data: data,
                processData: false,
                contentType: false,
                beforeSend: () => _this.SaveBtn.startLoading(),
                success: () => {
                    $('#svc-dirty').prop('hidden', true);
                    _this.SaveBtn.stopLoading().then(() => _this.SaveBtn.showSuccess());
                },
                error: () => _this.SaveBtn.stopLoading().then(() => _this.SaveBtn.showError()),
            });
        });
    },

    /**
     * ONE place decides what a numeric field accepts, and the markup declares it with `inputmode`.
     *
     * Before this, the only masked fields in the area were the service's own price and duration.
     * Every per-service override, every field in the add-question panel and both preview fields
     * took free text — and the server "cleaned" it by stripping the letters, so typing "fdds20"
     * silently saved 20 and "3gddg0" silently saved 30. A plausible wrong number is worse than an
     * error, so this stops it at the keyboard and SaveServiceRequest rejects it at the door.
     *
     *   inputmode="decimal" -> money and percentages: digits and at most 2 decimal places
     *   inputmode="numeric" -> minutes: whole numbers only
     *
     * Delegated on body and scoped to this feature area's surfaces, so rows the panel injects
     * later are covered without re-binding, and no other screen's fields are touched.
     */
    /**
     * The grip is a drag handle AND a menu. Owner, 2026-07-26: on a list longer than the screen
     * you cannot see the edge you are dropping against, so clicking the handle offers Move up /
     * Move down. Ends are disabled rather than silently doing nothing.
     */
    /**
     * The grip really drags now.
     *
     * It carried draggable="true" and a "Drag to reorder" tooltip, but nothing on the services
     * table ever listened for dragover — so the browser started a drag, refused every drop target,
     * and the row snapped back. A handle that says it drags and does not is worse than no handle.
     */
    initServiceRowDrag() {
        const tbody = document.querySelector('.services-table tbody');
        if (!tbody) return;

        let dragged = null;

        // The handle is what the user grabs, but a table ROW is what has to move, so the row is
        // what we mark and what we insert.
        tbody.addEventListener('dragstart', (e) => {
            const handle = e.target.closest('.bb-grip__handle');
            if (!handle) return;
            dragged = handle.closest('.bb-svc-row');
            if (!dragged) return;
            dragged.classList.add('is-dragged');
            e.dataTransfer.effectAllowed = 'move';
            // Firefox refuses to start a drag unless something is written.
            e.dataTransfer.setData('text/plain', dragged.dataset.id || '');
        });

        tbody.addEventListener('dragover', (e) => {
            if (!dragged) return;
            const over = e.target.closest('.bb-svc-row');
            if (!over || over === dragged) return;
            // Without preventDefault the browser treats this as "no drop allowed" — which is
            // exactly what was happening before.
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            const box = over.getBoundingClientRect();
            const below = e.clientY > box.top + box.height / 2;
            over.parentNode.insertBefore(dragged, below ? over.nextSibling : over);
        });

        const finish = () => {
            if (!dragged) return;
            dragged.classList.remove('is-dragged');
            dragged = null;
            this.persistServiceOrder();
        };

        tbody.addEventListener('drop', (e) => { e.preventDefault(); finish(); });
        tbody.addEventListener('dragend', finish);
    },

    /** Write the list's current order down. One POST for the whole list, drag or move-up/down. */
    persistServiceOrder() {
        const ids = $('.bb-svc-row').map((i, el) => $(el).data('id')).get().filter(Boolean);
        if (!ids.length) return;
        // Keep data-sort honest so the move-up/down bounds checks still read the right thing.
        $('.bb-svc-row').each((i, el) => $(el).attr('data-sort', i + 1));
        $.ajax({
            method: 'POST',
            url: '/services-management/services/reorder-services',
            data: { ids },
        });
    },

    initReorderGrips() {
        $('body').off('click.grip').on('click.grip', function (e) {
            const handle = $(e.target).closest('.bb-grip__handle');
            const insideMenu = $(e.target).closest('.bb-grip__menu').length;

            // A move button closes the menu too. Its own list handler runs first and does the
            // reordering; closing here keeps that in ONE place instead of every list remembering.
            if (insideMenu) {
                if ($(e.target).closest('.bb-grip__move').length) {
                    setTimeout(() => {
                        $('.bb-grip').removeClass('is-open').find('.bb-grip__menu').prop('hidden', true);
                    }, 0);
                }
                return;
            }
            if (!handle.length) {
                $('.bb-grip').removeClass('is-open').find('.bb-grip__menu').prop('hidden', true);
                return;
            }

            e.preventDefault();
            const grip = handle.closest('.bb-grip');
            const wasOpen = grip.hasClass('is-open');
            $('.bb-grip').removeClass('is-open').find('.bb-grip__menu').prop('hidden', true);
            if (wasOpen) return;

            // Grey out the direction that has nowhere to go. Three lists use this grip now: the
            // services table, the question list in the editor, and the option rows inside the
            // attribute-set modal.
            const row = grip.closest('tr, .bb-svc-q, .attributes-edit-row');
            const sel = row.is('tr') ? 'tr' : (row.hasClass('bb-svc-q') ? '.bb-svc-q' : '.attributes-edit-row');
            const prev = row.prev(sel);
            // The starting option is pinned: nothing may be moved above it.
            const blockedByFirst = prev.length && prev.hasClass('is-first');
            grip.find('[data-sort-direction="up"]').prop('disabled', !prev.length || !!blockedByFirst);
            grip.find('[data-sort-direction="down"]').prop('disabled', !row.next(sel).length);

            grip.addClass('is-open').find('.bb-grip__menu').prop('hidden', false);
            handle.attr('aria-expanded', 'true');

            // The menu lives inside `.scroll-container-table`, which is overflow:auto — so an
            // absolutely-positioned menu gets CLIPPED by it, and on a narrow window it was cut in
            // half. Pin it to the viewport instead, which no ancestor's overflow can touch.
            const menu = grip.find('.bb-grip__menu')[0];
            const h = handle[0].getBoundingClientRect();
            menu.style.position = 'fixed';

            // ⛔ `position: fixed` does NOT mean "relative to the viewport" here. The sidebar puts
            // transform: translateX(120px) on the main container, and a transformed ancestor
            // becomes the containing block for fixed children — so left:218px painted at 338px.
            // Rather than hard-code the sidebar's offset, park the menu at 0,0, measure where that
            // actually landed, and correct by the difference. Works under any transform, and keeps
            // the menu in the DOM where the click handlers expect to find it.
            menu.style.left = '0px';
            menu.style.top = '0px';
            const origin = menu.getBoundingClientRect();
            const wantLeft = Math.min(h.left, window.innerWidth - origin.width - 8);
            const below = h.bottom + 4;
            const wantTop = (below + origin.height > window.innerHeight - 8)
                ? h.top - origin.height - 4
                : below;
            menu.style.left = Math.round(wantLeft - origin.left) + 'px';
            menu.style.top = Math.round(wantTop - origin.top) + 'px';
        });

        // A pinned menu does not travel with the page, so it would sit where it was opened.
        // Closing on scroll is what every menu in the app does anyway.
        $(window).off('scroll.grip resize.grip').on('scroll.grip resize.grip', () => {
            $('.bb-grip.is-open').removeClass('is-open').find('.bb-grip__menu').prop('hidden', true);
        });

        $(document).on('keydown.grip', (e) => {
            if (e.key === 'Escape') {
                $('.bb-grip').removeClass('is-open').find('.bb-grip__menu').prop('hidden', true);
            }
        });
    },

    initInputMasks() {
        // Each container gets its OWN suffixed selector. Joining the containers into one string
        // first and appending " input[…]" only suffixes the LAST one — the other three then match
        // the container div, and the handler writes the container's (undefined) value back into
        // whatever input bubbled the event, wiping the field as you type.
        const AREAS = ['.bb-svc-editor', '#addq', '#product-modal', '#attributes-modal'];
        const within = (suffix) => AREAS.map((a) => a + ' ' + suffix).join(', ');

        // Minutes are whole numbers, but the "." must still be TYPEABLE while you are in the field.
        // Dropping it outright (what the shared maskInteger does) turns "30.5" into 305 as you
        // type — a silent ten-fold error, the same class of bug this pass exists to close. So the
        // dot survives keystrokes and the field truncates at it on blur: 30.5 -> 30, never 305.
        const maskMinutes = (ev) => {
            const el = ev.target;
            let v = String(el.value).replace(/[^0-9.]/g, '');
            const dot = v.indexOf('.');
            if (dot !== -1) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
            if (el.value !== v) el.value = v;
        };

        $('body').on('input', within('input[inputmode="decimal"]'), masks.maskNumber);
        $('body').on('input', within('input[inputmode="numeric"]'), maskMinutes);

        // Legacy service form outside those containers (the admin industry catalog embeds it bare).
        $('body').on('input', "input[name='price']", masks.maskNumber);
        $('body').on('input', "input[name='base_work_duration']", maskMinutes);

        // Leaving the field settles it: a trailing or lone "." goes, an empty field reads 0, and a
        // minutes field drops everything after the point. What you see is then what gets saved.
        const settle = function () {
            let v = String(this.value).trim();
            if (this.getAttribute('inputmode') === 'numeric') {
                v = v.split('.')[0];
            } else if (v.endsWith('.')) {
                v = v.slice(0, -1);
            }
            this.value = (v === '' || v === '.') ? '0' : v;
        };
        $('body').on('blur', within('input[inputmode="decimal"]'), settle);
        $('body').on('blur', within('input[inputmode="numeric"]'), settle);
        $('body').on('blur', "input[name='price'], input[name='base_work_duration']", settle);
    },

    getServicesModal() {
        let _this = this;
        $('.services-list__table-controls-button-add').click(function (e) {
            e.preventDefault();
            $.ajax({
                type: 'GET',
                url: '/services-management/services/get-form',
                success: function (response) {
                    $('#form-container').html(response);
                    $('#product-modal').modal('show');
                    _this.initServiceAdd();
                    restartLivewireTree('#product-modal');
                }
            });
        });
    },
    getActionServiceModal() {
        let _this = this;
        $('.services-list__table-controls-row-item').click(function (e) {
            let action = $(this).data('action');
            if (action == 'delete')
                return true;

            e.preventDefault();

            let product_id = $(this).data('product-id');
            let category_id = $(this).data('category-id');
            if (action) {
                switch (action) {
                    case 'edit':
                        $.ajax({
                            type: 'GET',
                            url: '/services-management/services/get-form',
                            data: {
                                product_id
                            },
                            success: function (response) {
                                $('#form-container').html(response);
                                $('#product-modal').modal('show');
                                _this.initServiceAdd();
                                _this.initSortMods();
                                _this.initSortToggleBtns();
                                restartLivewireTree('#product-modal');
                            }
                        });
                        break;
                    case 'view':
                        $.ajax({
                            type: 'GET',
                            data: {
                                product_id
                            },
                            url: '/services-management/services/get-complete-form/' + category_id,
                            success: function (response) {
                                $('#form-container').html(response);
                                $('#service-request-modal').modal('show');

                                refreshLivewireTree('#service-request-modal');
                                dispatchLivewireEvent('getSelectedService', [product_id]);
                                dispatchLivewireEvent('categoriesHidden');

                            }
                        });
                        break;
                }
            }
        });
    },
    /**
     * Put a rejected save back in front of the person who made it: a danger banner at the top of
     * the form listing what the server refused, and a red edge on each field it named. Clearing
     * happens on the next attempt, so a fixed field stops looking broken.
     */
    clearFormErrors() {
        const form = $('#product-form');
        form.find('.bb-input, .bb-select').removeClass('is-invalid');
        form.find('.form-validation-errors').empty();
        $('.product-form-wrapper .form-validation-errors').empty();
    },

    showFormErrors(xhr) {
        const form = $('#product-form');
        const box = form.find('.form-validation-errors').add($('.product-form-wrapper .form-validation-errors'));
        form.find('.bb-input, .bb-select').removeClass('is-invalid');
        box.empty();

        const payload = xhr && xhr.responseJSON;
        const errors = payload && (payload.errors || (payload.error ? { form: [payload.error] } : null));
        if (!errors) {
            box.html('<div class="bb-banner bb-banner--danger"><span class="bb-banner__ic">!</span><div>That did not save. Please try again.</div></div>');
            return;
        }

        const lines = [];
        Object.keys(errors).forEach((field) => {
            form.find('[name="' + field + '"]').addClass('is-invalid');
            [].concat(errors[field]).forEach((m) => lines.push($('<div>').text(m).html()));
        });
        box.html('<div class="bb-banner bb-banner--danger"><span class="bb-banner__ic">!</span><div>' + lines.join('<br>') + '</div></div>');
        form.find('.is-invalid').first().trigger('focus');
    },

    initServiceAdd() {
        $('#service-submit-form').click((e) => {
            this.SaveBtn = new SaveButton();
            e.preventDefault();
            this.clearFormErrors();
            //let data = $('#product-form').serialize();
            let data = new FormData($('#product-form')[0]);
            if($('input[name=tax_exempt]:checked').length)
                data.delete('tax_id')
            
            const selectedWorkers = $(`#product-form .bb-workers-select .worker-row input[type=checkbox]:checked`);
            for (let selectedWorker of selectedWorkers) {
                data.append('workers[]', $(selectedWorker).attr('id'));
            }
            //console.log(data.get()); return;
            $.ajax({
                type: 'POST',
                url: '/services-management/services/save-service',
                data: data,
                processData: false,
                contentType: false,
                beforeSend: () => {
                    this.SaveBtn.startLoading();
                },
                success: (response) => {
                    this.SaveBtn.stopLoading().then(
                        () => {
                            this.SaveBtn.showSuccess().then(
                                function () {
                                    // A service the quick dialog just created is only half set up,
                                    // so Save carries on into the editor rather than closing onto a
                                    // list the user then has to search for their own new row in.
                                    if (response && response.edit_url) {
                                        window.location = response.edit_url;
                                        return;
                                    }
                                    $('#product-modal').modal('hide');
                                    window.location.reload();
                                }
                            )
                        }
                    );
                },
                error: (xhr) => {
                    // Saving a service with no title returned 422 and the page said nothing: the
                    // code that rendered the reason had been commented out, so the button flashed
                    // and the dialog sat there looking fine. The server already names the field and
                    // the problem — show it, and mark the field it is about.
                    this.showFormErrors(xhr);
                    this.SaveBtn.stopLoading().then(() => this.SaveBtn.showError());
                }
            });
        });
    },
    initSortMods() {
        $('.product-sort-btn').on('click', ev=> {
            let currentRow = $($(ev.currentTarget).parents('.product-mod-row')[0])

            const direction = $(ev.currentTarget).attr('data-sort-direction');
            const modifierId = currentRow.attr('data-mod-id');
            const serviceId = currentRow.attr('data-product-id');
            const sort = Number(currentRow.attr('data-sort'));

            let maxSort = sort
            let minSort = sort
            $('.product-mod-row').each(function(){
                let rowSort = Number($(this).attr('data-sort'))
                if(!isNaN(rowSort)){
                    if (maxSort < rowSort)
                        maxSort = rowSort
                    if (minSort > rowSort)
                        minSort = rowSort
                }
            })
            let directElem = null
            if(direction == 'up' && sort != minSort && currentRow.prev('.product-mod-row')){
                directElem = currentRow.prev('.product-mod-row')
                directElem.before(currentRow)
            }else if(direction == 'down' && sort != maxSort && currentRow.next('.product-mod-row')){
                directElem = currentRow.next('.product-mod-row')
                directElem.after(currentRow)
            }else{
                return
            }
            currentRow.attr('data-sort', directElem.attr('data-sort'))
            directElem.attr('data-sort', sort)
            currentRow.addClass('move')
            setTimeout(()=>currentRow.removeClass('move'), 300)
            // The row moved out from under the open menu; close it so it does not float
            // over a different service than the one it belongs to.
            $('.bb-grip').removeClass('is-open').find('.bb-grip__menu').prop('hidden', true)
        });
    },

    initSortToggleBtns(){
        $('.product-mod-checkbox').on('change', ev=>{
            const checkbox = $(ev.currentTarget)
            const checked = checkbox.is(':checked')
            const currentRow = $(checkbox.parents('.product-mod-row')[0])
            let directRow = null
            if(checked){
                directRow = $($('.product-mod-row .product-mod-checkbox:checked').not(checkbox).last().parents('.product-mod-row')[0])
                currentRow.find('.product-sort-btns').removeClass('d-none')
            }else{
                directRow = $($('.product-mod-row .product-mod-checkbox:checked').last().parents('.product-mod-row')[0])
                currentRow.find('.product-sort-btns').addClass('d-none')
                currentRow.attr('data-sort', '')
            }
            if(directRow)
                directRow.after(currentRow)

            $('.product-mod-row .product-mod-checkbox:checked').each((index,el)=>{
                let row = $($(el).parents('.product-mod-row')[0])
                row.attr('data-sort', index+1)
            })
        })
    },

    recordsToShowChange() {
        let _this = this;
        $('select[name="records_to_show"]').change(function (e) {
            let form = $(this).parents('form');
            if (form)
                form.submit();
        });
    },
    /**
     * Replaces initProductDeletes(), which was dead three times over: init() never called it, it
     * posted to /delete-service/{id} which is not a route, and it reloaded a DataTable that is not
     * on this page. The real delete was the bare <a href> next to it — one click, no question.
     */
    confirmDeleteService() {
        $('body').off('click', '.services-list__table-controls-row-item[data-action="delete"]')
            .on('click', '.services-list__table-controls-row-item[data-action="delete"]', function (e) {
                e.preventDefault();
                e.stopPropagation();
                let id = $(this).data('product-id');
                let title = $(this).data('service-title') || 'this service';
                let modal = $('#service-delete-confirm');
                // "This cannot be undone" was never true — the delete has always set deleted_at and
                // kept the row, its questions and its per-service prices. The blade default was
                // corrected first and had no effect, because this line overwrites it on every open.
                modal.find('.confirm-text').text(
                    'Delete “' + title + '”? It comes off your booking form straight away. Nothing is '
                    + 'lost — it moves to Archived, keeps its questions and prices, and can be restored. '
                    + 'Appointments already booked are not affected.'
                );
                modal.find('.confirm-delete-service').attr('data-id', id);
                modal.modal('show');
            });

        $('body').off('click', '.confirm-delete-service')
            .on('click', '.confirm-delete-service', function () {
                let id = $(this).attr('data-id');
                if (!id) return;
                let btn = $(this).attr('disabled', true);
                $.ajax({
                    type: 'POST',
                    url: '/services-management/services/delete-service/' + id,
                    data: {
                        _method: 'DELETE',
                        _token: $('meta[name="csrf-token"]').attr('content'),
                    },
                    success: function () { location.reload(true); },
                    error: function () {
                        btn.removeAttr('disabled');
                        $('#service-delete-confirm').find('.confirm-text')
                            .text('That could not be deleted. Reload the page and try again.');
                    }
                });
            });
    },

    /**
     * Copy a service with its questions. There was no way to do this at all, so a near-identical
     * service meant re-attaching every attribute set by hand.
     */
    duplicateService() {
        $('body').off('click', '.bb-svc-duplicate')
            .on('click', '.bb-svc-duplicate', function (e) {
                e.preventDefault();
                e.stopPropagation();
                let btn = $(this);
                if (btn.attr('disabled')) return;
                btn.attr('disabled', true);
                $.ajax({
                    type: 'POST',
                    url: '/services-management/services/duplicate-service/' + btn.data('service-id'),
                    data: { _token: $('meta[name="csrf-token"]').attr('content') },
                    success: function () { location.reload(true); },
                    error: function () { btn.removeAttr('disabled'); }
                });
            });
    },

    templateEdit() {
        $('#sub-container').on('click', '.template-edit-btn', function (e) {
            let templateId = e.target.getAttribute('data-template-id')

            $.ajax({
                type: 'GET',
                url: `/jobs-flow/template/${templateId}/edit`,

                success: (response) => {
                    $('#sub-container').html(response);
                    $('#checklist-template-form-modal').modal('show');

                    const formId = $('#checklist-template-form-modal').find('.template-edit-form').attr('id');
                    $('#checklist-template-form-modal [data-dismiss="modal"]').on('click', '.close-btn', () => {
                        window.close()
                    });
                    $('#checklist-template-form-modal .add-another-line').on('click', () => {
                        addTemplateLine(formId)
                    });
                    $('#checklist-template-form-modal').on('click', '.remove-line', (e) => {
                        removeTemplateLine(e.target)
                    })
                }
            })

           function addTemplateLine(formId) {
                $(`#${formId}`).append(window.formLine);
            }

            function removeTemplateLine(target) {
                const formId = $(target).parents('form').attr('id');
                $(target).parents('.item').remove();
            }

        })
    },

    templateDelete() {
        $('#sub-container').on('click', '.template-delete-btn', (e) => {
            let templateId = e.target.getAttribute('data-template-id')
            $.ajax({
                method: 'DELETE',
                url: `/jobs-flow/template/${templateId}`,

                success: function (response) {
                    $('#checklist-templates-modal').modal('hide')
                    $('#product-modal').modal('hide')

                }
            });

        });
    },
    templateUpdate() {
        $('#sub-container').on('click', '.update-template-btn', (e) => {
            let templateId = e.target.getAttribute('data-template-id')
            const template_title = $('#sub-container').find('#template_title').val()
            let userData = {
                items: [],
            }

            const items = $(`#sub-container .item`);
            for (let item of items) {
                const itemJQ = $(item);
                if (itemJQ.find(`[name=title]`).val().trim().length > 0 && itemJQ.find(`[name=details]`).val().trim().length > 0) {
                    userData.items.push({
                        id: itemJQ.find(`[name=item_id]`).val(),
                        title: itemJQ.find(`[name=title]`).val().trim(),
                        details: itemJQ.find(`[name=details]`).val().trim()
                    });
                }
            }

            $.ajax({
                url: `/jobs-flow/template/${templateId}/update`,
                type: "POST",
                data: {userData, template_title},
                success: function (data) {
                    $('#checklist-template-form-modal').modal('hide');
                    $('#checklist-templates-modal').modal('hide');
                    $('#product-modal').modal('hide')


                },
                error: function (msg) {
                    $('#checklist-template-form-modal').modal('hide');
                }
            });


        });

    },

    templateView() {
        $('#form-container').on('click', '.template-view', (e) => {
            let template_create_button = e.target.getAttribute('data-template-create_button')
            let template_hide_use_as = e.target.getAttribute('data-template-hide_use_as')
            let template_type = e.target.getAttribute('data-template-type')

            $.ajax({
                type: 'GET',
                url: `/jobs-flow/template/show/${template_type}`,
                data: {
                    'template_create_button': template_create_button,
                    'template_hide_use_as': template_hide_use_as
                },
                success: (response) => {
                    $('#sub-container').html(response);
                    $('#checklist-templates-modal').modal('show');
                }
            });

        })
    },

    initFormActions(){
        $('body').on('click', '#service_tax_check', function() {
            if ($(this).is(':checked')) {
                $('.tax-container').addClass('d-none');
            }else{
                $('.tax-container').removeClass('d-none');
            }
        });

        $('body').on('click', '#service_payment_check', function() {
            if ($(this).is(':checked')) {
                $('.tips-block').removeClass('d-none');
                $('.tax-block').removeClass('d-none');
            } else {
                $('.tips-block').addClass('d-none');
                $('.tax-block').addClass('d-none');
            }
        });

        $('body').on('click', '#checklist_select_show', function() {
            if ($(this).is(':checked')) {
                $('.checklist-select').removeClass('d-none');
            }else{
                $('.checklist-select').addClass('d-none');
                $('select [name="checklist_template_id"]').val('')
            }
        });

    },

    /**
     * Reordering services. The endpoint and the ordering were both alive the whole time — every
     * customer-facing service list orders by `sort` — but the previous build removed the only
     * control that fired this, so the order could not be changed at all. The grip brings it back.
     */
    sortServices(){
        $('body').off('click', '.sort-service-btn').on('click','.sort-service-btn', ev=> {
            // `this` in an arrow function is the module, not the button — the disabled check
            // silently never fired. The menu greys out an unavailable direction anyway.
            if ($(ev.currentTarget).is('[disabled]')) {
                return;
            }

            let currentRow = $($(ev.currentTarget).parents('.service-row')[0])
            const direction = $(ev.currentTarget).attr('data-sort-direction');
            const serviceId =  currentRow.attr('data-id');
            const sort = Number(currentRow.attr('data-sort'));
            let maxSort = sort
            let minSort = sort
            $('.service-row').each(function(){
                let rowSort = Number($(this).attr('data-sort'))
                if(maxSort<rowSort)
                    maxSort = rowSort
                if(minSort>rowSort)
                    minSort = rowSort
            })
            let directElem = null
            if(direction == 'up' && sort != minSort && currentRow.prev('.service-row')){
                directElem = currentRow.prev('.service-row')
                directElem.before(currentRow)
            }else if(direction == 'down' && sort != maxSort && currentRow.next('.service-row')){
                directElem = currentRow.next('.service-row')
                directElem.after(currentRow)
            }else{
                return
            }
            currentRow.attr('data-sort', directElem.attr('data-sort'))
            directElem.attr('data-sort', sort)
            currentRow.addClass('move')
            setTimeout(()=>currentRow.removeClass('move'), 300)

            $.ajax({
                method: 'POST',
                url: '/services-management/services/sort-service',
                data: {
                    serviceId,
                    directId: directElem.data('id'),
                },
                success: (response) =>{

                },
                error: (response)=>{

                }
            })
        });
    },

    dropDownField(){
        $('body').off('click', '.dropdown-field-title')
        .on('click', '.dropdown-field-title', function(e) {
            const dropDown = $(this).parent();
            if ($(dropDown).find('.dropdown-field-content').hasClass('opened')) {
                $(dropDown).find('.dropdown-field-content').removeClass('opened');
                $(this).find('.dropdown-field-arrow').removeClass('opened');
            } else {
                $('.dropdown-field-content.opened').removeClass('opened');
                $('.dropdown-field-arrow').removeClass('opened');
                $(dropDown).find('.dropdown-field-content').addClass('opened');
                $(this).find('.dropdown-field-arrow').addClass('opened');
            }
        });
    }
}
